import CloseIcon from '@mui/icons-material/Cancel'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import SaveIcon from '@mui/icons-material/Save'
import { Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Fab, FormControl, Grid, IconButton, InputAdornment, InputLabel, List, ListItem, ListItemText, ListSubheader, MenuItem, OutlinedInput, Popover, Select, SelectChangeEvent, TextField, Tooltip, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { FC, useState } from 'react'
import { HardwareType, MarkupPropsType, MarkupType, MaterialGroupType, MaterialType, materialTypeValues } from '../../api/appAPI'
import emptyMaterial from '../../assets/emptyMaterial.png'
import { hardwareParamTypeValues } from '../../classes/Hardware'
import OutlinedDiv from '../../common/OutlineDiv'
import { EnqueueSnackbarType, showMessage } from '../common/Helpers'
import { HardwareMUPAWithUnitSupport, InputWithoutSubmitWithWeightUnitSupport } from '../common/UnitComponents'
import QuestionIcon from '@mui/icons-material/HelpOutline'

const hardwareTypesNeededWarning = [
    { type: 'length', actualUnit: 'cm' },
    { type: 'area', actualUnit: 'cm²' },
    { type: 'drawerBox', actualUnit: 'cm²' },
    { type: 'cornerHorizontalSheet', actualUnit: 'cm²' },
    { type: 'cornerTwoDoor', actualUnit: 'cm²' },
]

export type ChipType = { id: number, name: string }
type MChipType = { id: number, name: string, img: string, isGroup: false }
type MGChipType = { id: number, name: string, materials: { id: number, name: string, img: string, isGroup: false }[], isGroup: true }
export type MaterialChipType = MChipType | MGChipType

type TextfieldProps = {
    value: string,
    onChange(event: React.ChangeEvent<{ value: unknown }>): void
}

type SelectProps = {
    value: string,
    onChange(event: SelectChangeEvent<string>): void
}

type NumberTextfieldProps = {
    value: number,
    onChange(v: number): void
}

interface IProps {
    title: string
    name: TextfieldProps
    sku: TextfieldProps
    description: TextfieldProps
    type: SelectProps
    paramType: SelectProps
    unitWeight: NumberTextfieldProps
    unitPrice: TextfieldProps
    unitPriceAddon: {
        value: string,
        onChange(v: string): void
    }
    addonD: TextfieldProps
    addonP: TextfieldProps
    minPrice: TextfieldProps
    markupId: SelectProps

    markups: MarkupType[]
    hardwareList: HardwareType[]
    materials: MaterialType[]
    materialGroups: MaterialGroupType[]

    button: {
        text: string,
        onClick(): void
    }

    childrenChips: {
        chips: Array<ChipType>
        deleteChip(chip: ChipType): void
        addChip(chip: ChipType): void
    }

    materialsChips: {
        chips: Array<MaterialChipType>
        deleteChip(chip: MaterialChipType): void
        addChip(chip: MaterialChipType): void
    }

    defaultMaterialId: {
        value: number
        set(id: number): void
    }

    needSave?: boolean
    editable: boolean

    addMarkup(markup: MarkupPropsType, enqueueSnackbar: EnqueueSnackbarType): void
    openMaterialDetails(id: number): void
    openMaterialGroupDetails(id: number): void
}

const HardwareUIBox: FC<IProps> = props => {

    const [newMarkupName, setNewMarkupName] = useState('')
    const [newMarkupValue, setNewMarkupValue] = useState('')
    const [newMarkupDialogOpen, setNewMarkupDialogOpen] = useState(false)

    const [materialSelectOpen, setMaterialSelectOpen] = useState(false)
    const [anchorEl] = useState<HTMLButtonElement | null>(null)

    const { enqueueSnackbar } = useSnackbar()

    // const handleNewMarkupClick = () => {
    //     setNewMarkupDialogOpen(true)
    // }

    const handleNewMarkupDialogClose = () => {
        setNewMarkupName('')
        setNewMarkupValue('')
        setNewMarkupDialogOpen(false)
    }

    const handleNewMarkupCreateClick = () => {
        if (newMarkupName === '') {
            showMessage(enqueueSnackbar, "Enter markup name")
            return
        }

        if (props.markups.find(m => m.name === newMarkupName)) {
            showMessage(enqueueSnackbar, "Markup with name '" + newMarkupName + "' already exits")
            return
        }

        if (newMarkupValue === '') {
            showMessage(enqueueSnackbar, "Enter markup value")
            return
        }

        props.addMarkup({
            name: newMarkupName,
            value: parseFloat(newMarkupValue)
        }, enqueueSnackbar)
        handleNewMarkupDialogClose()
    }

    const handleChildrenClick = (chip: ChipType) => {
        // if (props.childrenChips.chips.find(ch => ch.id === chip.id)) {
        //     props.childrenChips.deleteChip(chip)
        // }
        // else {
        props.childrenChips.addChip(chip)
        // }
    }
    const handleMaterialClick = (chip: MaterialChipType) => {
        if (props.materialsChips.chips.find(ch => ch.id === chip.id)) {
            props.materialsChips.deleteChip(chip)
        }
        else {
            props.materialsChips.addChip(chip)
        }
    }

    return (
        <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            pb={2}
            style={{
                width: 400,
                height: 'fit-content',
                margin: 15,
                border: '1px solid black',
                maxHeight: '90%',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
            }}
        >
            {/* <Backdrop
                style={{ position: 'sticky', zIndex: 11, height: '100%', width: '100%' }}
                open={props.unEditable ? true : false}
            ></Backdrop> */}
            <Box
                py={1}
                style={{
                    position: 'sticky',
                    top: 0,
                    width: '100%',
                    backgroundColor: 'white',
                    zIndex: 10
                }}>
                <Grid container spacing={2}>
                    <Grid item xs={4}> </Grid>
                    <Grid item xs={4} container justifyContent='center' alignItems='center'>
                        <Typography variant='h6'>{props.title}</Typography>
                    </Grid>
                    <Grid item xs={4} container justifyContent='flex-end' alignItems='center'>
                        <Tooltip title={props.title === 'New Part' ? 'Create' : 'Save'}>
                            <Fab
                                color={props.needSave ? 'secondary' : "primary"}
                                size='small'
                                onClick={props.button.onClick}
                                style={{ marginRight: 10 }}
                            >
                                {props.title === 'New Part' ? <NoteAddIcon /> : <SaveIcon />}
                            </Fab>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>

            <TextField
                label="Part Name"
                variant="outlined"
                value={props.name.value}
                onChange={props.name.onChange}
                style={{ width: '60%', marginTop: 15 }}
                disabled={!props.editable}
            />
            <TextField
                label="Part SKU"
                variant="outlined"
                value={props.sku.value}
                onChange={props.sku.onChange}
                style={{ width: '60%', marginTop: 15 }}
                disabled={!props.editable}
            />
            <TextField
                label="Part description"
                multiline
                minRows={3}
                variant="outlined"
                value={props.description.value}
                onChange={props.description.onChange}
                style={{ width: '60%', marginTop: 15 }}
                disabled={!props.editable}
            />
            <FormControl
                variant="outlined"
                style={{ width: '60%', marginTop: 15 }}
                disabled={!props.editable}
            >
                <InputLabel>Select type</InputLabel>
                <Select
                    value={props.type.value}
                    onChange={props.type.onChange}
                    label="Select type"
                >
                    <MenuItem value={'static'}>Static</MenuItem>
                    <MenuItem value={'parametric'}>Parametric</MenuItem>
                </Select>
            </FormControl>

            {
                props.type.value === 'parametric' &&
                <FormControl
                    variant="outlined"
                    style={{ width: '60%', marginTop: 15 }}
                    disabled={!props.editable}
                >
                    <InputLabel>Select parameter type</InputLabel>
                    <Select
                        value={props.paramType.value}
                        onChange={props.paramType.onChange}
                        label="Select parameter type"
                    >
                        {
                            hardwareParamTypeValues.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)
                        }
                    </Select>
                </FormControl>
            }

            {/* <Box display='flex' alignItems='center' style={{ width: '60%', marginTop: 15, columnGap: 15 }}> */}
            {/* <FormControl variant="outlined" style={{ width: '60%', marginTop: 15, }}>
                <InputLabel htmlFor="outlined-adornment-amount">Unit Weight</InputLabel>
                <OutlinedInput
                    label="Unit Weight"
                    startAdornment={<InputAdornment position="start">kg</InputAdornment>}
                    type='number'
                    value={props.unitWeight.value}
                    onChange={props.unitWeight.onChange}
                    disabled={props.materialsChips.chips.length > 0 || !props.editable}
                />
            </FormControl> */}
            <InputWithoutSubmitWithWeightUnitSupport
                sx={{ width: .6, mt: 2 }}
                label='Unit weight'
                valueInKg={props.unitWeight.value}
                onChange={props.unitWeight.onChange}
                disabled={props.materialsChips.chips.length > 0 || !props.editable}
                endAdornment={hardwareTypesNeededWarning.map(h => h.type).includes(props.paramType.value) ? <Tooltip sx={{ maxWidth: 200 }} placement='top' title={`This weight will apply for per ${hardwareTypesNeededWarning.find(h => h.type === props.paramType.value)?.actualUnit || ''}, regardless of the measurement unit you choose`}>
                    <QuestionIcon color='secondary' />
                </Tooltip> : undefined}
            />
            <FormControl variant="outlined" style={{ width: '60%', marginTop: 15, }}>
                <InputLabel htmlFor="outlined-adornment-amount">Unit Price</InputLabel>
                <OutlinedInput
                    label="Unit Price"
                    startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    endAdornment={hardwareTypesNeededWarning.map(h => h.type).includes(props.paramType.value) ? <Tooltip placement='top' title={`This price will apply for per ${hardwareTypesNeededWarning.find(h => h.type === props.paramType.value)?.actualUnit || ''}, regardless of the measurement unit you choose`}>
                        <QuestionIcon color='secondary' />
                    </Tooltip> : undefined}
                    type='number'
                    value={props.unitPrice.value}
                    onChange={props.unitPrice.onChange}
                    disabled={props.materialsChips.chips.length > 0 || !props.editable}
                />
            </FormControl>
            {(props.paramType.value === 'area' || props.paramType.value === 'cornerHorizontalSheet' || props.paramType.value === 'cornerTwoDoor') && <>
                {/* <FormControl variant="outlined" style={{ width: '60%', marginTop: 15, }}>
                    <InputLabel htmlFor="outlined-adornment-amount">Machining Unit Price Addon</InputLabel>
                    <OutlinedInput
                        label="Machining Unit Price Addon"
                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                        type='number'
                        value={props.unitPriceAddon.value}
                        onChange={props.unitPriceAddon.onChange}
                        disabled={!props.editable}
                    />
                </FormControl> */}
                <HardwareMUPAWithUnitSupport
                    valueInCm={parseFloat(props.unitPriceAddon.value)}
                    onChange={v => {
                        props.unitPriceAddon.onChange(v.toString())
                    }}
                    sx={{ width: .6, mt: 2 }}
                />
                <FormControl variant="outlined" style={{ width: '60%', marginTop: 15, }}>
                    <InputLabel htmlFor="outlined-adornment-amount">Addon Per Piece</InputLabel>
                    <OutlinedInput
                        label="Addon Per Piece"
                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                        type='number'
                        value={props.addonD.value}
                        onChange={props.addonD.onChange}
                        disabled={!props.editable}
                    />
                </FormControl>
                <FormControl variant="outlined" style={{ width: '60%', marginTop: 15, }}>
                    <InputLabel htmlFor="outlined-adornment-amount">Addon</InputLabel>
                    <OutlinedInput
                        label="Addon"
                        startAdornment={<InputAdornment position="start">%</InputAdornment>}
                        type='number'
                        value={props.addonP.value}
                        onChange={props.addonP.onChange}
                        disabled={!props.editable}
                    />
                </FormControl>
                <FormControl variant="outlined" style={{ width: '60%', marginTop: 15, }}>
                    <InputLabel htmlFor="outlined-adornment-amount">Minimum Price</InputLabel>
                    <OutlinedInput
                        label="Minimum Price"
                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                        type='number'
                        value={props.minPrice.value}
                        onChange={props.minPrice.onChange}
                        disabled={!props.editable}
                    />
                </FormControl>
            </>}

            {/* <span>
                    <Button variant='contained' color='primary' onClick={(e) => {
                        setMaterialSelectOpen(true)
                        setAnchorEl(e.currentTarget)
                    }}>Material</Button>
                </span>
            </Box> */}
            <FormControl
                variant="outlined"
                style={{ width: '60%', marginTop: 15 }}
                disabled={!props.editable}
            >
                <InputLabel>Select markup</InputLabel>
                <Select
                    value={props.markups.find(m => m.id.toString() === props.markupId.value)?.name || ''}
                    onChange={props.markupId.onChange}
                    label="Select markup"
                >
                    {props.markups.map(m => <MenuItem key={m.id} value={m.name}>{m.name} - {m.value}</MenuItem>)}
                    {/* <MenuItem onClick={handleNewMarkupClick}>New Markup</MenuItem> */}
                </Select>
            </FormControl>
            {
                props.type.value === 'parametric' && <>
                    <Divider
                        style={{ width: '80%', marginTop: 15, marginBottom: 5 }}
                    />
                    <Typography>Materials</Typography>

                    <Box
                        component="ul"
                        px={1}
                        my={1}
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            columnGap: 10,
                            rowGap: 10,
                            flexWrap: 'wrap',
                            listStyle: 'none'
                        }}>
                        {props.materialsChips.chips.sort((a, b) => {
                            if (a.isGroup === b.isGroup) return 0
                            else if (a.isGroup) return 1
                            else return -1
                        }).map((chip) =>
                            <li key={chip.id}>
                                {chip.isGroup
                                    ? <OutlinedDiv
                                        // p={2}
                                        // my={1}
                                        // style={{
                                        //     // border: '1px solid black',
                                        //     // borderRadius: 30,
                                        //     display: 'flex',
                                        //     justifyContent: 'center',
                                        //     alignItems: 'center'
                                        // }}
                                        // onClick={e => {
                                        //     if (e.ctrlKey)
                                        //         props.openMaterialGroupDetails(chip.id)
                                        // }}
                                        label={chip.name}
                                    ><Box
                                        style={{
                                            // border: '1px solid black',
                                            // borderRadius: 30,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                        onClick={e => {
                                            if (e.ctrlKey)
                                                props.openMaterialGroupDetails(chip.id)
                                        }}
                                    >
                                            <Box
                                                component="ul"
                                                p={0}
                                                // my={1}
                                                style={{
                                                    // border: '1px solid black',
                                                    // borderRadius:30,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    columnGap: 10,
                                                    rowGap: 10,
                                                    flexWrap: 'wrap',
                                                    listStyle: 'none'
                                                }}>
                                                {chip.materials.map(childChip => <li key={`${chip.id}_${childChip.id}`}>
                                                    <Chip
                                                        label={childChip.name}
                                                        avatar={<Avatar alt="Material Icon" src={childChip.img ? childChip.img : emptyMaterial} />}
                                                        color={childChip.id === props.defaultMaterialId.value ? 'primary' : undefined}
                                                        onClick={(e) => {
                                                            if (e.ctrlKey)
                                                                props.openMaterialDetails(childChip.id)
                                                            else if (props.editable)
                                                                props.defaultMaterialId.set(childChip.id)
                                                        }}
                                                    />
                                                </li>)}
                                            </Box>
                                            <IconButton edge="end" onClick={() => { if (props.editable) props.materialsChips.deleteChip(chip) }}>
                                                <CloseIcon />
                                            </IconButton>
                                        </Box>
                                    </OutlinedDiv>
                                    : <Chip
                                        label={chip.name}
                                        avatar={<Avatar alt="Material Icon" src={chip.img ? chip.img : emptyMaterial} />}
                                        onDelete={() => { if (props.editable) props.materialsChips.deleteChip(chip) }}
                                        color={chip.id === props.defaultMaterialId.value ? 'primary' : undefined}
                                        onClick={(e) => {
                                            if (e.ctrlKey)
                                                props.openMaterialDetails(chip.id)
                                            else if (props.editable)
                                                props.defaultMaterialId.set(chip.id)
                                        }}
                                    />
                                }

                            </li>)}
                    </Box>

                    <FormControl
                        variant="outlined"
                        style={{ width: '60%', marginTop: 15 }}
                        disabled={!props.editable}
                    >
                        <InputLabel>Select materials</InputLabel>
                        <Select
                            value={''}
                            // onChange={props.markup.onChange}
                            label="Select materials"
                        >
                            <ListSubheader>Groups</ListSubheader>
                            {
                                props.materialGroups.map(m => <MenuItem key={m.id} onClick={() => {
                                    const mGroup = props.materialGroups.find(mg => mg.id === m.id)

                                    if (mGroup)
                                        handleMaterialClick({
                                            id: m.id,
                                            name: m.name,
                                            materials: props.materials.filter(m => mGroup.materialsIds.includes(m.id)).map(m => ({ id: m.id, name: m.name, img: m.thumbnail, isGroup: false })),
                                            isGroup: true
                                        })
                                }}>{m.name}</MenuItem>)
                            }
                            {
                                materialTypeValues.map(type => {
                                    const materials = props.materials.filter(m => m.type === type)

                                    return <Box key={type}>
                                        <ListSubheader>{type}</ListSubheader>
                                        {
                                            materials.map(m => <MenuItem key={m.id} onClick={() => {
                                                handleMaterialClick({ id: m.id, name: m.name, img: m.thumbnail, isGroup: false })
                                            }}>{m.name}</MenuItem>)
                                        }
                                    </Box>
                                })
                            }
                        </Select>
                    </FormControl>
                </>
            }
            <Divider
                style={{ width: '80%', marginTop: 15, marginBottom: 5 }}
            />
            <Typography>Children</Typography>

            <Box
                component="ul"
                px={1}
                my={1}
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    columnGap: 10,
                    rowGap: 10,
                    flexWrap: 'wrap',
                    listStyle: 'none'
                }}>
                {props.childrenChips.chips.map((chip, i) =>
                    <li key={`${chip.id}_${i}`}>
                        <Chip
                            label={chip.name}
                            onDelete={() => { if (props.editable) props.childrenChips.deleteChip(chip) }}
                        // color='primary'
                        // variant='outlined'
                        />
                    </li>)}
            </Box>

            <FormControl
                variant="outlined"
                style={{ width: '60%', marginTop: 15 }}
                disabled={!props.editable}
            >
                <InputLabel>Select children</InputLabel>
                <Select
                    value={''}
                    // onChange={props.markup.onChange}
                    label="Select children"
                >
                    {props.hardwareList.map(h => <MenuItem key={h.id} onClick={() => {
                        handleChildrenClick({ id: h.id, name: h.name })
                    }}>{h.name}</MenuItem>)}
                </Select>
            </FormControl>

            <Dialog
                open={newMarkupDialogOpen}
            >
                <DialogTitle>New Markup</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Markup Name"
                        variant="outlined"
                        value={newMarkupName}
                        onChange={(event) => {
                            setNewMarkupName(event.target.value)
                        }}
                        style={{ marginTop: 15 }}
                    />
                    <TextField
                        label="Markup Value"
                        variant="outlined"
                        value={newMarkupValue}
                        onChange={(event) => {
                            setNewMarkupValue(event.target.value)
                        }}
                        type='number'
                        style={{ marginLeft: 15, marginTop: 15 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleNewMarkupDialogClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleNewMarkupCreateClick} color="primary">
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Popover
                open={materialSelectOpen}
                anchorEl={anchorEl}
                onClose={() => { setMaterialSelectOpen(false) }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <List style={{ minWidth: 250 }}>
                    {props.materials.map(m => <ListItem
                        key={m.id}
                        button
                        onClick={() => {
                            // props.addHardwareToItem(props.item.name, m.id)
                            setMaterialSelectOpen(false)
                        }}>
                        <ListItemText primary={m.name} />
                    </ListItem>
                    )}
                </List>
            </Popover>
        </Box >
    )
}

export default HardwareUIBox